import React, { useContext, useState } from 'react'
import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import ExpandMore from '@mui/icons-material/ExpandMore'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { GlobalContext } from '../../../App'
import S3Document from '../../../components/images/S3Document'

// Building blocks for the reviewer's application page: collapsible section
// cards with flat label/value fields (no bubble cards), shared with the
// References panel.

export const yesNo = (value) =>
  value === true ? 'Yes' : value === false ? 'No' : null

const isEmpty = (value) =>
  value === undefined || value === null || value === ''

export const FieldLabel = ({ children }) => (
  <Typography
    component="div"
    sx={{
      color: 'text.secondary',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.06em',
      fontSize: '0.68rem',
      mb: 0.25,
    }}
  >
    {children}
  </Typography>
)

export const FieldValue = ({ value }) =>
  React.isValidElement(value) ? (
    value
  ) : (
    <Typography
      variant="body2"
      sx={{
        fontWeight: 600,
        color: isEmpty(value) ? 'text.disabled' : 'text.primary',
        whiteSpace: 'pre-line',
        overflowWrap: 'anywhere',
      }}
    >
      {isEmpty(value) ? '—' : value}
    </Typography>
  )

export const AccordComponent = ({ title, caption, chip, defaultOpen, children }) => (
  <Accordion disableGutters defaultExpanded={!!defaultOpen}>
    <AccordionSummary expandIcon={<ExpandMore />}>
      <Stack
        direction="row"
        alignItems="center"
        gap={1}
        flexWrap="wrap"
        minWidth={0}
      >
        <Typography variant="title">{title}</Typography>
        {chip}
        {caption && (
          <Typography variant="body2" sx={{ width: '100%' }}>
            {caption}
          </Typography>
        )}
      </Stack>
    </AccordionSummary>
    <AccordionDetails>
      <Grid container spacing={2}>
        {children}
      </Grid>
    </AccordionDetails>
  </Accordion>
)

export const Line = ({ title, value, children, fullWidth }) => (
  <Grid item xs={12} sm={fullWidth ? 12 : 6} xl={fullWidth ? 12 : 4}>
    <FieldLabel>{title}</FieldLabel>
    {(!isEmpty(value) || !children) && <FieldValue value={value} />}
    {children}
  </Grid>
)

export const SubHeading = ({ children, action }) => (
  <Grid item xs={12}>
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      gap={1}
      pt={1}
      borderTop={1}
      borderColor="divider"
    >
      <Typography fontWeight={600} color="text.primary">
        {children}
      </Typography>
      {action}
    </Stack>
  </Grid>
)

// A value the reviewer will paste elsewhere (DVLA check, DBS check…)
export const CopyValue = ({ value, label, mono }) => {
  const { setToast } = useContext(GlobalContext)
  const [copied, setCopied] = useState(false)
  if (isEmpty(value)) return <FieldValue value={value} />
  return (
    <Stack direction="row" alignItems="center" gap={0.5}>
      <Typography
        variant="body2"
        sx={{
          fontWeight: 700,
          color: 'text.primary',
          ...(mono && { fontFamily: 'monospace', fontSize: '0.95rem', letterSpacing: '0.12em' }),
        }}
      >
        {value}
      </Typography>
      <Tooltip title={copied ? 'Copied' : `Copy ${label ?? ''}`.trim()}>
        <IconButton
          size="small"
          onClick={() => {
            navigator.clipboard?.writeText(String(value)).then(() => {
              setCopied(true)
              setToast({ message: `${label ?? 'Value'} copied` })
              setTimeout(() => setCopied(false), 1500)
            })
          }}
        >
          <ContentCopyIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
    </Stack>
  )
}

export const DocumentList = ({ documents }) =>
  documents?.length ? (
    <Stack direction="row" flexWrap="wrap" columnGap={3} rowGap={1} mt={0.5}>
      {documents.map((doc) => (
        <S3Document key={doc} item={doc} />
      ))}
    </Stack>
  ) : (
    <Box>
      <FieldValue value={null} />
    </Box>
  )
